import { useState } from "react";
import { Checkbox } from "@a2z/ui";
import type { SellerPermission } from "@/lib/api/types";
import { useLocale } from "@/lib/i18n/intl";
import { tokens } from "./primitives";
import {
  permissionGroupLabel,
  permissionLabel,
  tierHelper,
  tierLabel,
  uiText,
} from "./rbac-labels";
import {
  applyTier,
  resolveTier,
  type AreaDef,
  type TierKey,
} from "./permission-tiers";

type Props = {
  area: AreaDef;
  selected: Set<SellerPermission>;
  onChange: (next: Set<SellerPermission>) => void;
};

// One area = one access-tier row. The tier control is a segmented track with
// large (>=44px) targets so it's thumb-friendly on mobile and identical on
// desktop. A "Customize" expander reveals the raw permission checkboxes; a
// non-ladder combo resolves to the "custom" state.
export function AreaAccessRow({ area, selected, onChange }: Props) {
  const locale = useLocale();
  const resolved = resolveTier(area, selected);
  const isCustom = resolved === "custom";
  const granted = area.perms.some((p) => selected.has(p));
  const [expanded, setExpanded] = useState(isCustom);

  const setTier = (tier: TierKey): void =>
    onChange(applyTier(selected, area, tier));

  const togglePerm = (permission: SellerPermission): void => {
    const next = new Set(selected);
    if (next.has(permission)) next.delete(permission);
    else next.add(permission);
    onChange(next);
  };

  return (
    <div
      style={{
        flexShrink: 0,
        borderRadius: 12,
        border: `1px solid ${granted ? tokens.accent : tokens.border}`,
        // Subtle tint when the area grants something — scannable down the list.
        background: granted
          ? `color-mix(in oklab, ${tokens.accent} 4%, white)`
          : tokens.surface,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: 14,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {/* Area name + custom badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <span
            style={{ fontSize: 14, fontWeight: 700, color: tokens.text }}
          >
            {permissionGroupLabel(area.group, locale)}
          </span>
          {isCustom ? (
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: 999,
                background: `color-mix(in oklab, ${tokens.accent} 14%, white)`,
                color: tokens.accent2,
              }}
            >
              {tierLabel("custom", locale)}
            </span>
          ) : null}
        </div>

        {/* Tier selector — a segmented track, full width, big touch targets. */}
        <div
          role="radiogroup"
          aria-label={permissionGroupLabel(area.group, locale)}
          style={{
            display: "flex",
            gap: 4,
            padding: 4,
            borderRadius: 11,
            background: tokens.bgSoft,
          }}
        >
          {area.tiers.map((tier) => {
            const active = resolved === tier;
            const isGrantTier = tier !== "none";
            return (
              <button
                key={tier}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setTier(tier)}
                style={{
                  flex: 1,
                  minHeight: 44,
                  padding: "8px 6px",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 12.5,
                  fontWeight: 700,
                  textAlign: "center",
                  background: active
                    ? isGrantTier
                      ? tokens.accent
                      : tokens.surface
                    : "transparent",
                  color: active
                    ? isGrantTier
                      ? "#fff"
                      : tokens.text
                    : tokens.text2,
                  boxShadow: active
                    ? "0 1px 2px rgba(15,20,40,0.12)"
                    : "none",
                  transition: "background 0.15s, color 0.15s",
                }}
              >
                {tierLabel(tier, locale)}
              </button>
            );
          })}
        </div>

        {/* Helper for the current state + show/hide permissions toggle */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 11.5,
              color: tokens.text3,
              lineHeight: 1.4,
              flex: 1,
              minWidth: 0,
            }}
          >
            {tierHelper(isCustom ? "custom" : resolved, locale)}
          </span>
          {area.perms.length > 1 ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                fontSize: 11.5,
                fontWeight: 700,
                color: tokens.accent2,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                whiteSpace: "nowrap",
                padding: 0,
                flexShrink: 0,
              }}
            >
              {uiText(expanded ? "hidePermissions" : "showPermissions", locale)}
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block",
                  transform: expanded ? "rotate(180deg)" : "none",
                  transition: "transform 0.15s",
                  fontSize: 9,
                }}
              >
                {"▾"}
              </span>
            </button>
          ) : null}
        </div>
      </div>

      {/* Raw permissions (progressive disclosure) */}
      <div
        style={{
          display: "grid",
          gridTemplateRows: expanded ? "1fr" : "0fr",
          transition: "grid-template-rows 0.2s ease",
        }}
      >
        <div style={{ overflow: "hidden" }}>
          <div
            style={{
              borderTop: `1px solid ${tokens.border}`,
              background: tokens.surface,
              padding: "6px 14px 10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {area.perms.map((permission) => (
              <label
                key={permission}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  minHeight: 44,
                  cursor: "pointer",
                  fontSize: 12.5,
                  color: tokens.text2,
                }}
              >
                <Checkbox
                  size="sm"
                  checked={selected.has(permission)}
                  onChange={() => togglePerm(permission)}
                />
                {permissionLabel(permission, locale)}
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
